import { cx } from "@linaria/core";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import type { PageDescriptor } from "@pm2/api";
import { AccessCodes, getPageContent } from "@pm2/api";
import type { PageAST } from "@pm2/content-engine";
import {
  inferUserAccessFromAST,
  parsePageXML,
  pm2CurrentAST,
  useContentEnginePrinters,
} from "@pm2/content-engine";
import {
  CourseRevisionPrinterProvider,
  getAllRevisionsQuery,
  useRevisedContentAST,
} from "@pm2/course-revisions";
import { verticalPaddingCls } from "@pm2/react-utils";
import type { PM2SitemapStore } from "@pm2/sitemap-utils";
import {
  findCourseDescriptorFromParents,
  pm2SitemapStore,
} from "@pm2/sitemap-utils";
import { pm2ConfigStore, type PM2ConfigStore } from "@pm2/store";
import {
  MissingAccessMessage,
  EmptyPage,
  NotFound,
  FrontpageGraphic,
} from "@pm2/ui-components";
import { BottomNavigation } from "@pm2/ui-layout";
import {
  FavouritesPortalPage,
  getFavouritesQuery,
  getLastLocationDescriptorQuery,
  selectIsFavouritesEnabled,
} from "@pm2/user-area";
import {
  useUserIdentityState,
  type UserIdentity,
} from "@provider/user-identity";
import { speechSelectors } from "@ui/kit";
import { printAST } from "@utils/ast";
import { prefetchQueryIfNeeded } from "@utils/common";
import { useKatex } from "@utils/react";

import { CourseTableOfContents } from "./atom.course-table-of-contents";
import { FrontpageTopsection } from "./page.content.frontpage-topsection";
import * as css from "./page.content.style";

type Props = {
  descriptor?: PageDescriptor;
  ast: PageAST | undefined;
};

export function ContentPage(props: Props): React.JSX.Element {
  const sitemap = pm2SitemapStore.useSelector((state) => state);
  const enableTableOfContentsForCourses = pm2ConfigStore.useSelector(
    (state) => state.config.settings.enableTableOfContentsForCourses,
  );
  const isLoggedIn = useUserIdentityState((state) => !!state.user);
  const isFavouritesEnabled = pm2SitemapStore.useSelector(
    selectIsFavouritesEnabled,
  );
  const { data: hasLastLocation } = useQuery(
    getLastLocationDescriptorQuery.useQueryConfig({
      queryConfig: { select: (data) => !!data?.descriptor },
    }),
  );

  const containerRef = useRef<HTMLDivElement>(null);
  useKatex(containerRef, [props.descriptor?.id]);

  const courseDescriptor = findCourseDescriptorFromParents(
    sitemap,
    props.descriptor,
  );
  const revisedAST = useRevisedContentAST(
    props.ast ?? [],
    props.descriptor,
    courseDescriptor?.id,
  );

  const { printers, interceptor } = useContentEnginePrinters();

  const hasAccess =
    (props.descriptor === undefined ||
      props.descriptor.accessCode === AccessCodes.ALLOWED) &&
    inferUserAccessFromAST(revisedAST);

  const isPageEmpty = revisedAST.length === 0;

  useEffect(() => {
    pm2CurrentAST.current = revisedAST;
  }, [revisedAST]);

  if (!props.ast) {
    return (
      <>
        <NotFound />

        {props.descriptor && (
          <BottomNavigation pageDescriptor={props.descriptor} />
        )}
      </>
    );
  }

  if (isPageEmpty) {
    return (
      <>
        <EmptyPage />

        {props.descriptor && (
          <BottomNavigation pageDescriptor={props.descriptor} />
        )}
      </>
    );
  }

  const children = printAST(revisedAST, printers, interceptor);
  const frontpageTopSection = props.descriptor?.frontPageTopSection;
  const withFavouritesSection =
    isLoggedIn && hasLastLocation && isFavouritesEnabled;

  return (
    <>
      <article
        ref={containerRef}
        className={cx(
          "gPortal-page",
          css.articleCls,
          verticalPaddingCls,
          speechSelectors.enableSpeech.className,
        )}
      >
        {props.descriptor?.type === "ThemePortalFrontPage" && (
          <>
            {withFavouritesSection ? (
              <FavouritesPortalPage
                blurredBackgroundImageSrc={frontpageTopSection?.image.src}
              />
            ) : null}

            {frontpageTopSection && (
              <FrontpageTopsection
                label={frontpageTopSection.label}
                heading={frontpageTopSection.headline}
                description={frontpageTopSection.description}
                image={frontpageTopSection.image}
                withBackground={!withFavouritesSection}
              />
            )}

            {!frontpageTopSection && !withFavouritesSection && (
              <FrontpageGraphic />
            )}
          </>
        )}

        {!hasAccess && <MissingAccessMessage />}
        {children}
        {props.descriptor && enableTableOfContentsForCourses && (
          <CourseTableOfContents pageDescriptor={props.descriptor} />
        )}
      </article>

      {props.descriptor && (
        <BottomNavigation pageDescriptor={props.descriptor} />
      )}
    </>
  );
}

export async function renderContentPage(
  user: UserIdentity | null | undefined,
  configStore: PM2ConfigStore,
  sitemapStore: PM2SitemapStore,
  queryClient: QueryClient,
  pageDescriptor: PageDescriptor,
): Promise<React.JSX.Element> {
  const configState = configStore.getState();
  const sitemapState = sitemapStore.getState();

  const courseDescriptor = findCourseDescriptorFromParents(
    sitemapState,
    pageDescriptor,
  );

  const [xml] = await Promise.all([
    getPageContent(configState.config.id, pageDescriptor.id, {
      previewMode: configState.previewMode,
    }),
    prefetchQueryIfNeeded(
      queryClient,
      getAllRevisionsQuery.queryConfig({
        user,
        courseId: courseDescriptor?.id,
      }),
    ),
    prefetchQueryIfNeeded(
      queryClient,
      getLastLocationDescriptorQuery.queryConfig({
        configStore,
        user,
        portalId: configState.config.id,
      }),
    ),
    prefetchQueryIfNeeded(
      queryClient,
      getFavouritesQuery.queryConfig({
        user,
        configStore,
        portalId: configState.config.id,
      }),
    ),
  ]);

  const ast = xml ? await parsePageXML(queryClient, xml, user) : undefined;

  return (
    <CourseRevisionPrinterProvider>
      <ContentPage descriptor={pageDescriptor} ast={ast} />
    </CourseRevisionPrinterProvider>
  );
}
